import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Pre-launch gate for the Financial Advisor longevity assessment.
 *
 * A build with VITE_LONGEVITY_ASSESSMENT_OPEN=true (UAT) always shows it. Any
 * other build (PROD until launch) hides it unless the dashboard is opened with
 * `?longevity-assessment`. The unlock is remembered in sessionStorage so it
 * survives in-app navigation and refresh, then clears when the tab closes (not
 * sticky forever like localStorage). sessionStorage can throw (private mode /
 * storage disabled) — fail safe.
 */
const LONGEVITY_FLAG_KEY = 'bquest_longevity_assessment_unlocked';

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(LONGEVITY_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberFlag(): void {
  try {
    sessionStorage.setItem(LONGEVITY_FLAG_KEY, '1');
  } catch {
    /* storage unavailable — the flag simply won't persist */
  }
}

/** True when the build opens the section, or this tab has unlocked it by URL. */
export function useLongevityFlag(): boolean {
  const [searchParams] = useSearchParams();
  const unlockNow = searchParams.has('longevity-assessment');
  const [remembered, setRemembered] = useState(readFlag);
  useEffect(() => {
    if (unlockNow) {
      rememberFlag();
      setRemembered(true);
    }
  }, [unlockNow]);
  const openByEnv = import.meta.env.VITE_LONGEVITY_ASSESSMENT_OPEN?.trim() === 'true';
  return openByEnv || unlockNow || remembered;
}
